<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="sequence-apply-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="sequence-apply-form" @submit.prevent="onSubmit">
        <div class="sequence-apply-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="sequence-apply-toolbar-title">{{
              tenantTexts.sequenceParticipantApplyRoutines.title
            }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container class="sequence-apply-card-container">
            <main-user-table
              :headers="headers"
              :items="computedSequenceDefinitions"
              :items-per-page="queryParams.pageSize"
              :server-items-length="
                sequenceDefinitionsPagination.totalCount || -1
              "
              :page="queryParams.page"
              :row-component="'sequence-definition-table-row'"
              @choose-sequence="chooseSequenceDefinition"
              @action:get-data="data => getDataAction({ ...data }, false)"
            />
          </v-container>
        </div>
        <v-container
          class="sequence-apply-card-footer d-flex justify-end pt-0 pb-4"
        >
          <v-row>
            <v-col>
              <dialog-date-picker
                label="Start date"
                name="startDate"
                :value="sequenceStartDate"
                :initial-date="$moment().format('YYYY-MM-DD')"
                @input="setDate"
              />
            </v-col>
            <v-col>
              <v-btn
                :disabled="!formIsValidCheck"
                rounded
                absolute
                bottom
                right
                elevation="0"
                color="primary"
                type="submit"
              >
                {{ tenantTexts.sequenceParticipantApplyRoutines.submit }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import DialogDatePicker from '@/components/forms/DialogDatePicker.vue'
import MainUserTable from '@/components/tables/MainUserTable'
import RouterMixin from '@/mixins/router'
import TenantTextsMixin from '@/mixins/tenantTexts'

const emptyForm = {}

export default {
  name: 'SequenceParticipantApplyDialog',
  components: {
    MainUserTable,
    DialogDatePicker,
  },
  mixins: [RouterMixin, TenantTextsMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    day: {
      type: [Boolean, Number, String],
      default: false,
    },
    sequenceDefinitions: {
      type: [Boolean, Object, Array],
      default: false,
    },
    sequenceDefinitionsPagination: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialog: false,
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
      sequenceStartDate: false,
      headers: [],
      formIsValidCheck: false,
      sequenceDate: false,
      sequenceId: false,
      defaultQueryParams: {
        query: '',
        pageSize: 50,
        page: 0,
      },
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        const defaultQuery = new URLSearchParams()
        for (const key in this.defaultQueryParams) {
          defaultQuery.append(key, this.defaultQueryParams[key])
        }
        this.getData(defaultQuery)
        this.sequenceStartDate = this.$moment().format('YYYY-MM-DD')
        this.sequenceDate = this.sequenceStartDate
      } else {
        this.dialog = false
      }
    },
  },
  computed: {
    computedSequenceDefinitions() {
      let sequences = []
      this.sequenceDefinitions.forEach(item => {
        let selected = false
        if (this.sequenceId == item.id) selected = true
        sequences.push({ selected: selected, ...item })
      })
      return sequences
    },
  },
  methods: {
    formIsValid() {
      if (this.sequenceDate && this.sequenceId) this.formIsValidCheck = true
      else this.formIsValidCheck = false
    },
    closeDialog() {
      this.dialog = false
      this.$emit('close-sequence-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    onSubmit() {
      this.$emit('on-submit', this.sequenceId, this.sequenceDate)
      this.closeDialog()
    },
    setDate(date) {
      this.sequenceDate = date.value
      this.formIsValid()
    },
    chooseSequenceDefinition(id) {
      this.sequenceId = id
      this.formIsValid()
    },
    getData(data = new URLSearchParams()) {
      if (this.dialog) {
        this.$emit('reload-sequences', data)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.sequence-apply {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    // looks little hacky
    > .v-card {
      width: 100%;
      height: 100%;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
      max-height: 100%;
      flex-shrink: 1;
      overflow: auto;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &-body {
      display: flex;
      flex-direction: column;
      min-height: 0;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
